import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';

// Listing Health — the queue (docs/redesign/02, Page 1).
//
// The one idea: revenue performance is the topline; every flag is a REASON
// revenue is off. So the queue ranks by DOLLARS AT STAKE, not an abstract
// 0-100 score, and every click lands on the calendar where the fix happens.
//
//   $ at stake = max(STLY-expected − OTB, 0)
//              + Σ smart-gap est. missed revenue
//              + price-gap upside (/api/airm/summary)
//
// KILLED per spec: the five filter tabs, the combined/quality/performance
// score columns and health rings, and the quick drawer (its flag cards move
// to the Property Window ▸ Why tab). Today links here with one count.
export class TriagePage {
  private container: HTMLElement;
  private items: any[] = [];
  private mix: Record<string, any> = {};
  private impact: any = null;
  private search = '';
  private censusSort: { key: string; dir: 1 | -1 } = { key: 'pace', dir: 1 };
  private stagedOnly = false;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  // ── derived per-listing economics ──────────────────────────
  private paceOf(it: any): number | null {
    const p = it.pace_vs_stly;
    if (p && p.pace_pct != null && Number.isFinite(Number(p.pace_pct))) return Number(p.pace_pct);
    return null;
  }
  private gapDollarsOf(it: any): number {
    const flags = [...(it.buckets?.performance?.flags || [])];
    const g = flags.find((f: any) => f.key === 'smart_gaps');
    return (g?.gaps || []).reduce((s: number, x: any) => s + (Number(x.est_missed_revenue) || 0), 0);
  }
  private atStakeOf(it: any): number {
    const p = it.pace_vs_stly || {};
    // No same-point-last-year baseline → the "behind STLY" term is meaningless; skip it.
    const behind = p.stly_status === 'new' ? 0 : Math.max(0, (Number(p.stly_otb_revenue) || 0) - (Number(p.otb_revenue) || 0));
    const m = this.mix[String(it.listing.id)] || {};
    return Math.round(behind + this.gapDollarsOf(it) + (Number(m.est_upside) || 0));
  }
  private flagsOf(it: any): any[] {
    const b = it.buckets;
    if (!b) return [];
    const order: Record<string, number> = { high: 0, warn: 1, info: 2 };
    return [...b.performance.flags, ...b.quality.flags].sort((x, y) => (order[x.severity] ?? 9) - (order[y.severity] ?? 9));
  }
  private inQueue(it: any): boolean {
    const pace = this.paceOf(it);
    const m = this.mix[String(it.listing.id)] || {};
    const underShare = m.open ? (m.under || 0) / m.open : 0;
    const high = this.flagsOf(it).some(f => f.severity === 'high');
    if (this.stagedOnly) return (m.staged || 0) > 0;
    // Under 60 days live there is too little history to judge pace or mix —
    // only a high-severity flag earns a queue slot.
    const daysLive = it.listing?.days_live;
    if (daysLive != null && daysLive < 60) return high;
    return (pace != null && pace < 85) || (pace != null && pace >= 120 && underShare >= 0.25) || high;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">
        <div id="lh-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
      </div>`;
    const [triage, mix, impact] = await Promise.all([
      fetch('/api/triage').then(r => r.ok ? r.json() : []),
      fetch('/api/airm/summary').then(r => r.ok ? r.json() : {}),
      fetch('/api/impact').then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.items = (Array.isArray(triage) ? triage : []).filter((it: any) => it.buckets);
    this.mix = mix || {};
    this.impact = impact;
    this.draw();
  }

  private draw(): void {
    const body = document.getElementById('lh-body');
    if (!body) return;
    body.innerHTML = `
      <style>
        .lh-head { display:flex; align-items:baseline; gap:12px; margin-bottom:14px; }
        .lh-head h2 { margin:0; font-size:16px; font-weight:600; letter-spacing:-0.01em; }
        .lh-head .sub { font-size:12px; color:var(--text-muted); }
        .lh-search { margin-left:auto; padding:6px 12px; border:1px solid var(--border); border-radius:7px; font-size:12px; min-width:220px; }
        .lh-pulse { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:10px; margin-bottom:18px; }
        .lh-pulse-card { border:1px solid var(--border); border-radius:var(--radius); padding:13px 16px; background:#fff; }
        .lh-pulse-card.click { cursor:pointer; } .lh-pulse-card.click:hover { border-color:#d1d5db; }
        .lh-pulse-label { font-size:10px; font-weight:500; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .lh-pulse-value { font-size:24px; font-weight:600; margin-top:4px; line-height:1.1; }
        .lh-pulse-sub { font-size:11px; color:var(--text-faint, #9ca3af); margin-top:3px; }
        .lh-sec { display:flex; align-items:baseline; gap:10px; margin:4px 0 10px; }
        .lh-sec h3 { margin:0; font-size:14px; font-weight:600; }
        .lh-sec span { font-size:12px; color:var(--text-muted); }
        .lh-queue-wrap { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:22px; }
        table.lh-queue { table-layout:fixed; width:100%; min-width:940px; }
        table.lh-queue th { white-space:normal; line-height:1.25; vertical-align:bottom; }
        table.lh-queue td { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-size:12px; }
        table.lh-queue td.r, table.lh-queue th.r { text-align:right; }
        .lh-stake { font-size:13px; font-weight:600; }
        .lh-fix { font-size:11px; font-weight:600; color:#fff; background:var(--text); border-radius:6px; padding:3px 10px; display:inline-block; }
        .lh-pill { font-size:10px; font-weight:600; padding:1px 8px; border-radius:999px; }
        .lh-mixbar { display:inline-flex; width:40px; height:5px; border-radius:3px; overflow:hidden; background:var(--bg-subtle); vertical-align:middle; }
        .lh-chip { font-size:10px; color:var(--text-muted); border:1px solid var(--border); border-radius:999px; padding:1px 7px; display:inline-block; max-width:100%; overflow:hidden; text-overflow:ellipsis; vertical-align:middle; }
        table.lh-census { table-layout:fixed; width:100%; min-width:880px; }
        table.lh-census th { white-space:normal; line-height:1.25; vertical-align:bottom; cursor:pointer; user-select:none; }
        table.lh-census td { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-size:12px; }
        table.lh-census td.r, table.lh-census th.r { text-align:right; }
        .lh-prop-name { font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .lh-prop-sub { font-size:11px; color:var(--text-muted); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .lh-pacebar { display:inline-block; width:36px; height:3px; border-radius:2px; background:var(--bg-subtle); vertical-align:middle; margin-left:6px; overflow:hidden; }
        .lh-row { cursor:pointer; }
        @media (max-width:1100px){ .lh-pulse { grid-template-columns:repeat(2,minmax(0,1fr)); } }
      </style>
      ${this.renderHead()}
      ${this.renderPulse()}
      ${this.renderQueue()}
      ${this.renderCensus()}
    `;
    this.bind();
  }

  private money(v: any): string { return '$' + Math.round(Number(v) || 0).toLocaleString(); }
  private moneyK(v: any): string { const n = Number(v) || 0; return Math.abs(n) >= 100000 ? '$' + Math.round(n / 1000) + 'k' : this.money(n); }
  // Census cells are width-budgeted (table-layout:fixed) — always k-compact: $8.4k, $112k.
  private moneyTight(v: any): string {
    const n = Math.round(Number(v) || 0);
    if (Math.abs(n) < 1000) return '$' + n.toLocaleString();
    const k = n / 1000;
    return '$' + (Math.abs(k) >= 100 ? String(Math.round(k)) : k.toFixed(1).replace(/\.0$/, '')) + 'k';
  }

  private renderHead(): string {
    return `
      <div style="display:flex; align-items:flex-end; justify-content:space-between; gap:14px; margin-bottom:18px;">
        <div class="dash-head" style="margin-bottom:0;">
          <h1>Priority Queue</h1>
          <p>Every listing ranked by dollars at stake — where revenue is off and why. Click anything to land on the calendar that fixes it.</p>
        </div>
        <input class="lh-search" id="lh-search" type="search" placeholder="Search property or city…" value="${Formatters.escapeHtml(this.search)}">
      </div>`;
  }

  // ── Block B — pulse strip ───────────────────────────────────
  private renderPulse(): string {
    // Same-store only: listings with no same-point-last-year baseline would
    // inflate the OTB side with nothing on the STLY side.
    let otb = 0, stly = 0, newN = 0, newOtb = 0;
    for (const it of this.items) {
      const p = it.pace_vs_stly || {};
      if (p.stly_status === 'new') { newN++; newOtb += Number(p.otb_revenue) || 0; continue; }
      otb += Number(p.otb_revenue) || 0;
      stly += Number(p.stly_otb_revenue) || 0;
    }
    const pacePct = stly > 0 ? Math.round(otb / stly * 100) : null;
    const queue = this.items.filter(it => this.inQueue(it));
    const behind = this.items.filter(it => { const p = this.paceOf(it); return p != null && p < 85; });
    const atStake = queue.reduce((s, it) => s + this.atStakeOf(it), 0);
    let over = 0, under = 0, open = 0, staged = 0;
    for (const k of Object.keys(this.mix)) {
      const m = this.mix[k];
      over += m.over || 0; under += m.under || 0; open += m.open || 0; staged += m.staged || 0;
    }
    const captured = this.impact?.captured_revenue ?? null;
    const landed = (this.impact?.ledger || []).filter((r: any) => r.status === 'landed').length;
    const card = (label: string, ico: string, value: string, sub: string, opts: { tone?: string; click?: string } = {}) => `
      <div class="kpi-card ${opts.tone || ''} ${opts.click ? 'clickable' : ''}" ${opts.click ? `data-lh-pulse="${opts.click}"` : ''}>
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${value}</div>
        <div class="kpi-card-cap">${sub}</div>
      </div>`;
    return `
      <div class="kpi-grid" style="grid-template-columns:repeat(5,minmax(0,1fr)); margin-bottom:20px;">
        ${card('Portfolio pace · vs STLY', 'trending', pacePct != null ? pacePct + '%' : '—', `${this.moneyK(otb)} on books vs ${this.moneyK(stly)} STLY${newN ? ` · excl. ${newN} new (${this.moneyK(newOtb)})` : ''}`, { tone: pacePct == null ? '' : pacePct >= 100 ? 'tone-good' : 'tone-bad' })}
        ${card('Behind pace (<85%)', 'flag', String(behind.length), `${this.moneyK(atStake)} at stake across the queue`, { tone: behind.length ? 'tone-bad' : 'tone-good' })}
        ${card('Mispriced nights · 60d', 'target', `${under}u · ${over}o`, open ? `${Math.round((over + under) / open * 100)}% of ${open.toLocaleString()} open nights` : 'no open nights')}
        ${card('Staged, not pushed', 'package', String(staged), staged ? 'click to filter the queue' : 'nothing staged', { click: 'staged' })}
        ${card('Captured · all time', 'wallet', captured != null ? this.moneyK(captured) : '—', `${landed} accept${landed === 1 ? '' : 's'} landed — proof in Impact`, { tone: 'tone-good' })}
      </div>`;
  }

  // ── Block C — the action queue, a dense ranked table ────────
  // One row per queue listing, ranked by $ at stake desc. table-layout:fixed
  // + colgroup (same approach as the census) so it can never overflow.
  private renderQueue(): string {
    const q = this.items
      .filter(it => this.inQueue(it))
      .filter(it => this.matches(it))
      .sort((a, b) => this.atStakeOf(b) - this.atStakeOf(a));
    if (!q.length) {
      return `<div class="lh-sec"><h3>Action queue</h3></div>
        ${emptyState('Nothing off pace', 'No listing is behind 85% pace, leaving money underpriced, or carrying a high flag. The census below is the full book.', '🎉')}`;
    }
    const rows = q.map(it => {
      const id = String(it.listing.id);
      const pace = this.paceOf(it);
      const m = this.mix[id] || {};
      const p = it.pace_vs_stly || {};
      const top = this.flagsOf(it)[0];
      const nf = this.flagsOf(it).length;
      const daysLive = it.listing?.days_live;
      const isNew = p.stly_status === 'new' || (daysLive != null && daysLive < 60);
      const newPill = `<span class="lh-pill" style="background:var(--bg-subtle); color:var(--text-muted);" ${it.listing?.online_since ? `title="online since ${Formatters.escapeHtml(it.listing.online_since)}, no same-point-last-year baseline"` : ''}>${daysLive != null ? `new · live ${daysLive}d` : 'new — no LY'}</span>`;
      const otbCell = p.stly_status === 'new'
        ? `<span class="mono num">${this.moneyTight(p.otb_revenue)}</span> ${newPill}`
        : `<span class="mono num">${this.moneyTight(p.otb_revenue)} <span style="color:var(--text-muted);">/ ${p.stly_otb_revenue == null ? '—' : this.moneyTight(p.stly_otb_revenue)}</span></span>`;
      const pacePill = isNew
        ? `<span class="lh-pill" style="background:var(--bg-subtle); color:var(--text-muted);">new</span>`
        : pace == null
        ? `<span class="lh-pill" style="background:var(--bg-subtle); color:var(--text-muted);">no LY</span>`
        : `<span class="lh-pill" style="background:${pace < 85 ? 'var(--danger-soft)' : pace >= 120 ? 'var(--success-soft)' : 'var(--warning-soft)'}; color:${pace < 85 ? 'var(--danger-text)' : pace >= 120 ? 'var(--success-text)' : 'var(--warning-text)'};">${pace}%</span>`;
      const mixCell = m.open
        ? `<span class="mono num" style="font-size:11px;">${m.under}u·${m.over}o</span>
           <span class="lh-mixbar" title="${m.under} underpriced · ${m.in_band} in band · ${m.over} overpriced (next 60d)${m.est_upside ? ` · ${this.moneyK(m.est_upside)} upside` : ''}">
             <span style="flex:${m.under || 0}; background:#93c5fd;"></span>
             <span style="flex:${m.in_band || 0}; background:var(--border-soft);"></span>
             <span style="flex:${m.over || 0}; background:#fcd34d;"></span>
           </span>`
        : '<span style="color:var(--text-faint, #9ca3af);">—</span>';
      const chipBg = top?.severity === 'high' ? 'var(--danger-soft)' : top?.severity === 'warn' ? 'var(--warning-soft)' : 'var(--bg-subtle)';
      const chipFg = top?.severity === 'high' ? 'var(--danger-text)' : top?.severity === 'warn' ? 'var(--warning-text)' : 'var(--text-muted)';
      const reasonCell = top
        ? `<span class="lh-chip" style="background:${chipBg}; color:${chipFg}; border-color:transparent;" title="${Formatters.escapeHtml(top.why || '')}">${Formatters.escapeHtml(top.title)}</span>`
        : '<span style="color:var(--text-muted); font-size:11px;">pace alone</span>';
      const flagsCell = m.staged
        ? `<span class="mono num">${nf}</span> · <span style="font-weight:600; color:var(--text);">${m.staged} staged</span>`
        : `<span class="mono num">${nf}</span> flag${nf === 1 ? '' : 's'}`;
      return `
        <tr class="lh-row" data-lh-open="${id}">
          <td title="${Formatters.escapeHtml(it.listing.name || it.listing.short_name || id)}">
            <div class="lh-prop-name">${Formatters.escapeHtml(it.listing.short_name || it.listing.name || id)}</div>
            <div class="lh-prop-sub">${Formatters.escapeHtml(it.listing.city || '')}${it.listing.bedrooms ? ` · ${it.listing.bedrooms}BR` : ''}</div>
          </td>
          <td class="r"><span class="lh-stake mono num">${this.moneyK(this.atStakeOf(it))}</span></td>
          <td class="r">${otbCell}</td>
          <td>${pacePill}</td>
          <td>${mixCell}</td>
          <td>${reasonCell}</td>
          <td style="font-size:11px; color:var(--text-muted);" title="${nf} flag${nf === 1 ? '' : 's'}${m.staged ? ` · ${m.staged} staged, not pushed` : ''}">${flagsCell}</td>
          <td class="r"><span class="lh-fix">Fix dates →</span></td>
        </tr>`;
    }).join('');
    return `
      <div class="lh-sec">
        <h3>Action queue</h3>
        <span>${q.length} listing${q.length === 1 ? '' : 's'} past threshold — behind 85% pace, ≥120% with underpriced nights, or carrying a high flag${this.stagedOnly ? ' · filtered to staged' : ''}</span>
        ${this.stagedOnly ? '<span><a id="lh-clear-staged" style="color:var(--info); cursor:pointer;">clear filter</a></span>' : ''}
      </div>
      <div class="table-scroll lh-queue-wrap">
        <table class="lh-queue">
          <colgroup>
            <col style="width:19%"><col style="width:9%"><col style="width:15%"><col style="width:8%"><col style="width:12%"><col style="width:18%"><col style="width:10%"><col style="width:9%">
          </colgroup>
          <thead><tr>
            <th>Property</th>
            <th class="r" title="behind-STLY shortfall + smart-gap missed revenue + price-gap upside">$ at stake</th>
            <th class="r" title="OTB revenue today vs same point last year">OTB / STLY</th>
            <th>Pace</th>
            <th title="underpriced · overpriced open nights, next 60 days">Mix 60d</th>
            <th>Top reason</th>
            <th>Flags</th>
            <th></th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  // ── Block D — the census ────────────────────────────────────
  private renderCensus(): string {
    const sortVal = (it: any): number => {
      const m = this.mix[String(it.listing.id)] || {};
      switch (this.censusSort.key) {
        case 'pace': { const p = this.paceOf(it); return p == null ? 9999 : p; }
        case 'otb': return Number(it.pace_vs_stly?.otb_revenue) || 0;
        case 'occ': return Number(it.metrics?.occ_30d) || 0;
        case 'adr': return Number(it.metrics?.adr) || 0;
        case 'mix': return (m.under || 0) + (m.over || 0);
        case 'review': return Number(it.listing?.review_score) || 99;
        default: return 0;
      }
    };
    const rows = this.items
      .filter(it => this.matches(it))
      .sort((a, b) => (sortVal(a) - sortVal(b)) * this.censusSort.dir);
    const arrow = (k: string) => this.censusSort.key === k ? (this.censusSort.dir === 1 ? ' ↑' : ' ↓') : '';
    const body = rows.map(it => {
      const id = String(it.listing.id);
      const m = this.mix[id] || {};
      const p = it.pace_vs_stly || {};
      const pace = this.paceOf(it);
      const occ = it.metrics?.occ_30d, mOcc = it.metrics?.market_occ_comp ?? it.metrics?.market_occ_30d;
      const adr = it.metrics?.adr, mAdr = it.metrics?.market_adr;
      const fullName = it.listing.name || it.listing.short_name || id;
      const underShare = m.open ? (m.under || 0) / m.open : 0;
      const paceColor = pace == null ? 'var(--text-faint, #9ca3af)'
        : pace < 85 ? 'var(--danger)'
        : pace >= 120 ? (underShare >= 0.25 ? 'var(--warning)' : 'var(--success)')
        : 'var(--text)';
      const paceCell = p.stly_status === 'new'
        ? `<span class="lh-pill" style="background:var(--bg-subtle); color:var(--text-muted);" title="online since ${Formatters.escapeHtml(it.listing.online_since || '—')}, no same-point-last-year baseline">new — no LY</span>`
        : pace == null ? `<span class="mono num" style="color:${paceColor};">—</span>`
        : `<span class="mono num" style="color:${paceColor};">${pace}%</span><span class="lh-pacebar"><span style="display:block; height:100%; width:${Math.max(4, Math.min(100, Math.round(pace / 1.5)))}%; background:${paceColor};"></span></span>`;
      const rs = Number(it.listing?.review_score) || 0;
      const top = this.flagsOf(it)[0];
      const chipBg = top?.severity === 'high' ? 'var(--danger-soft)' : top?.severity === 'warn' ? 'var(--warning-soft)' : 'var(--bg-subtle)';
      const chipFg = top?.severity === 'high' ? 'var(--danger-text)' : top?.severity === 'warn' ? 'var(--warning-text)' : 'var(--text-muted)';
      return `
        <tr class="lh-row" data-lh-open="${id}">
          <td title="${Formatters.escapeHtml(fullName)}">
            <div class="lh-prop-name">${Formatters.escapeHtml(it.listing.short_name || it.listing.name || id)}</div>
            <div class="lh-prop-sub">${Formatters.escapeHtml(it.listing.city || '')}${it.listing.bedrooms ? ` · ${it.listing.bedrooms}BR` : ''}</div>
          </td>
          <td class="r">${paceCell}</td>
          <td class="r num mono">${this.moneyTight(p.otb_revenue)} <span style="color:var(--text-muted);">/ ${p.stly_otb_revenue == null ? '—' : this.moneyTight(p.stly_otb_revenue)}</span></td>
          <td class="r num mono"><span style="color:${occ != null && mOcc != null ? (occ >= mOcc ? 'var(--success)' : 'var(--danger)') : 'var(--text)'};">${occ != null ? Math.round(occ) + '%' : '—'}</span> <span style="color:var(--text-muted);">/ ${mOcc != null ? Math.round(mOcc) + '%' : '—'}</span></td>
          <td class="r num mono">${adr ? '$' + Math.round(adr) : '—'} <span style="color:var(--text-muted);">/ ${mAdr ? '$' + Math.round(mAdr) : '—'}</span></td>
          <td class="r num mono" title="underpriced · overpriced open nights, next 60 days">${m.open ? `${m.under}u·${m.over}o` : '—'}</td>
          <td class="r num mono" style="color:${rs > 0 && rs < 4.8 ? 'var(--danger)' : 'var(--text)'};">${rs > 0 ? rs.toFixed(2) : '—'}</td>
          <td>${top ? `<span class="lh-chip" style="background:${chipBg}; color:${chipFg}; border-color:transparent;" title="${Formatters.escapeHtml(top.why || '')}">${Formatters.escapeHtml(top.title)}</span>` : ''}</td>
          <td style="color:var(--text-faint, #9ca3af);">›</td>
        </tr>`;
    }).join('');
    return `
      <div class="lh-sec"><h3>All listings</h3><span>the census — every property, sortable; row click opens its window</span></div>
      <div class="table-scroll" style="border:1px solid var(--border); border-radius:var(--radius);">
        <table class="lh-census">
          <colgroup>
            <col style="width:21%"><col style="width:9%"><col style="width:12%"><col style="width:11%"><col style="width:10%"><col style="width:7%"><col style="width:5%"><col style="width:22%"><col style="width:3%">
          </colgroup>
          <thead><tr>
            <th>Property</th>
            <th class="r" data-lh-sort="pace" title="OTB revenue today vs same point last year">Pace vs STLY${arrow('pace')}</th>
            <th class="r" data-lh-sort="otb">OTB / STLY${arrow('otb')}</th>
            <th class="r" data-lh-sort="occ">Occ next 30d / mkt${arrow('occ')}</th>
            <th class="r" data-lh-sort="adr">ADR next 30d / mkt${arrow('adr')}</th>
            <th class="r" data-lh-sort="mix">Mix 60d${arrow('mix')}</th>
            <th class="r" data-lh-sort="review">★${arrow('review')}</th>
            <th>Top reason</th><th></th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
  }

  private matches(it: any): boolean {
    const q = this.search.trim().toLowerCase();
    if (!q) return true;
    return `${it.listing?.name || ''} ${it.listing?.short_name || ''} ${it.listing?.city || ''}`.toLowerCase().includes(q);
  }

  private bind(): void {
    // Open the Property Window on its Calendar tab, carrying the ranked queue
    // order so prev/next arrows make triage a flow instead of round-trips.
    const open = (id: string) => {
      const ranked = this.items
        .filter(it => this.inQueue(it))
        .sort((a, b) => this.atStakeOf(b) - this.atStakeOf(a))
        .map(it => String(it.listing.id));
      sessionStorage.setItem('workup_queue', JSON.stringify(ranked));
      sessionStorage.setItem('workup_preselect', id);
      sessionStorage.setItem('workup_tab', 'propensity');
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'data-explorer' }));
    };
    this.container.querySelectorAll<HTMLElement>('[data-lh-open]').forEach(el =>
      el.addEventListener('click', () => open(el.dataset.lhOpen!)));
    this.container.querySelectorAll<HTMLElement>('[data-lh-sort]').forEach(th =>
      th.addEventListener('click', () => {
        const k = th.dataset.lhSort!;
        if (this.censusSort.key === k) this.censusSort.dir = this.censusSort.dir === 1 ? -1 : 1;
        else this.censusSort = { key: k, dir: 1 };
        this.draw();
      }));
    this.container.querySelector<HTMLElement>('[data-lh-pulse="staged"]')?.addEventListener('click', () => {
      this.stagedOnly = !this.stagedOnly;
      this.draw();
    });
    document.getElementById('lh-clear-staged')?.addEventListener('click', () => { this.stagedOnly = false; this.draw(); });
    let timer: ReturnType<typeof setTimeout> | null = null;
    document.getElementById('lh-search')?.addEventListener('input', (e) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        this.search = (e.target as HTMLInputElement).value;
        this.draw();
        const s = document.getElementById('lh-search') as HTMLInputElement | null;
        if (s) { s.focus(); s.setSelectionRange(s.value.length, s.value.length); }
      }, 200);
    });
  }
}
